import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { NotificationBell } from '@/components/notifications';
import { normalizeImageValue } from '@/services/api';
import { isDoctorAccount } from '@/lib/userRoles';
import { 
  Menu, 
  X, 
  User, 
  Calendar, 
  LogOut,
  Globe,
  ChevronDown,
  Heart,
  Bell,
  FileText,
  Settings,
  LayoutDashboard,
  Clock,
  Users,
  Star,
  Stethoscope,
  Sun,
  Moon,
  Building2
} from 'lucide-react';

const resolveUserAvatar = (user) =>
  normalizeImageValue(
    user?.avatar ||
    user?.image ||
    user?.profile_image ||
    user?.photo ||
    user?.patient?.image ||
    user?.patient?.avatar ||
    ''
  );

const withAvatarVersion = (imageUrl, version) => {
  if (
    !imageUrl ||
    !version ||
    imageUrl.startsWith('data:image') ||
    imageUrl.startsWith('blob:')
  ) {
    return imageUrl;
  }

  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}v=${version}`;
};

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { favoritesCount } = useFavorites();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/doctors', label: t('nav.doctors') },
    { to: '/specialties', label: t('nav.specialties') },
    // Pharmacy hidden until backend API is ready
    // { to: '/pharmacy', label: i18n.language === 'ar' ? 'الصيدلية' : 'Pharmacy' },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isDoctorUser = isDoctorAccount(user);
  const userAvatar = withAvatarVersion(
    resolveUserAvatar(user),
    user?.profile_image_updated_at
  );
  const userInitials =
    `${user?.first_name?.charAt(0) || ''}${user?.last_name?.charAt(0) || ''}` ||
    user?.name?.charAt(0) ||
    'U';
  const mobileMenuItemClass = 'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-teal-600 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-teal-400';
  const navigateFromMobileMenu = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleMobileLogout = () => {
    setMobileMenuOpen(false);
    handleLogout();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 dark:bg-slate-900/95 dark:border-slate-700 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-slate-900/80" data-testid="main-header">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="/images/logo.png" 
              alt="Dactrha" 
              className="h-10 w-10 rounded-lg object-contain"
            />
            <span className="text-xl font-bold text-slate-900 dark:text-white">{t('common.appName')}</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2">
            {!isDoctorUser && (
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                asChild
                data-testid="favorites-nav-btn"
              >
                <Link to="/favorites">
                  <Heart className="h-5 w-5 text-slate-600" />
                  {favoritesCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-xs">
                      {favoritesCount}
                    </Badge>
                  )}
                </Link>
              </Button>
            )}

            {/* Notifications - Only for logged in users */}
            {isAuthenticated && (
              <NotificationBell />
            )}

            {/* Dark Mode Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="theme-toggle-btn"
              title={isDark ? (i18n.language === 'ar' ? 'الوضع الفاتح' : 'Light mode') : (i18n.language === 'ar' ? 'الوضع الداكن' : 'Dark mode')}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1" data-testid="language-switcher">
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">{i18n.language === 'ar' ? 'العربية' : 'EN'}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLanguage('en')} data-testid="lang-en">
                  English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('ar')} data-testid="lang-ar">
                  العربية
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                    <Avatar className="h-8 w-8 border border-teal-100">
                      <AvatarImage src={userAvatar || undefined} className="object-cover" />
                      <AvatarFallback className="bg-teal-100 text-sm font-medium text-teal-700">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm">{user?.first_name || user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* Check if user is a doctor */}
                  {isDoctorUser ? (
                    <>
                      {/* Doctor Menu Items */}
                      <DropdownMenuItem onClick={() => navigate('/doctor/dashboard')} data-testid="doctor-dashboard-link">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/doctor/appointments')} data-testid="doctor-appointments-link">
                        <Calendar className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'مواعيدي' : 'My Appointments'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/doctor/availability')} data-testid="doctor-schedule-link">
                        <Clock className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'التوفر والخدمات' : 'Availability & Services'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/doctor/patients')} data-testid="doctor-patients-link">
                        <Users className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'مرضاي' : 'My Patients'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/doctor/reviews')} data-testid="doctor-reviews-link">
                        <Star className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'التقييمات' : 'Reviews'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/doctor/profile')} data-testid="doctor-profile-link">
                        <Stethoscope className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'الملف المهني' : 'My Profile'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/clinic/dashboard')} data-testid="clinic-dashboard-link">
                        <Building2 className="mr-2 h-4 w-4 text-teal-600" />
                        {i18n.language === 'ar' ? 'إدارة العيادة' : 'Clinic Management'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="doctor-settings-link">
                        <Settings className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'الإعدادات' : 'Settings'}
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      {/* Patient Menu Items */}
                      <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                        <User className="mr-2 h-4 w-4" />
                        {t('nav.myProfile')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/appointments')} data-testid="appointments-link">
                        <Calendar className="mr-2 h-4 w-4" />
                        {t('nav.appointments')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/family')} data-testid="family-link">
                        <Users className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'أفراد العائلة' : 'Family Members'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/medical-records')} data-testid="medical-records-link">
                        <FileText className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'السجلات الطبية' : 'Medical Records'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                        <Settings className="mr-2 h-4 w-4" />
                        {i18n.language === 'ar' ? 'الإعدادات' : 'Settings'}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" asChild data-testid="login-button">
                  <Link to="/login">{t('common.login')}</Link>
                </Button>
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700" asChild data-testid="register-button">
                  <Link to="/register">{t('common.register')}</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden max-h-[calc(100vh-4rem)] overflow-y-auto py-4 border-t dark:border-slate-700" data-testid="mobile-nav">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 hover:bg-slate-50 rounded-md dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-teal-400"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t my-2 pt-2 dark:border-slate-700">
                {!isDoctorUser && (
                  <Link
                    to="/favorites"
                    className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 hover:bg-slate-50 rounded-md flex items-center gap-2 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-teal-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Heart className="h-4 w-4" />
                    {i18n.language === 'ar' ? 'المفضلة' : 'Favorites'}
                    {favoritesCount > 0 && (
                      <Badge className="bg-red-500 text-xs">{favoritesCount}</Badge>
                    )}
                  </Link>
                )}
                {isAuthenticated && (
                  <Link
                    to="/notifications"
                    className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 hover:bg-slate-50 rounded-md flex items-center gap-2 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-teal-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Bell className="h-4 w-4" />
                    {i18n.language === 'ar' ? 'الإشعارات' : 'Notifications'}
                  </Link>
                )}
              </div>

              <div className="border-t my-2 pt-2 dark:border-slate-700">
                <Button
                  variant="ghost"
                  className="w-full justify-start px-3 text-slate-600 hover:text-teal-600 dark:text-slate-300 dark:hover:text-teal-400"
                  onClick={toggleTheme}
                  data-testid="mobile-theme-toggle-btn"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {isDark
                    ? (i18n.language === 'ar' ? 'الوضع الفاتح' : 'Light mode')
                    : (i18n.language === 'ar' ? 'الوضع الداكن' : 'Dark mode')}
                </Button>

                <div className="px-3 py-2">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                    <Globe className="h-4 w-4" />
                    {t('common.language')}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={i18n.language === 'en' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        changeLanguage('en');
                        setMobileMenuOpen(false);
                      }}
                      data-testid="mobile-lang-en"
                    >
                      English
                    </Button>
                    <Button
                      variant={i18n.language === 'ar' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        changeLanguage('ar');
                        setMobileMenuOpen(false);
                      }}
                      data-testid="mobile-lang-ar"
                    >
                      العربية
                    </Button>
                  </div>
                </div>
              </div>

              {isAuthenticated ? (
                <div className="border-t my-2 pt-2 dark:border-slate-700">
                  {isDoctorUser ? (
                    <>
                      <button type="button" onClick={() => navigateFromMobileMenu('/doctor/dashboard')} className={mobileMenuItemClass}>
                        <LayoutDashboard className="h-4 w-4" />
                        {t('nav.dashboard')}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/doctor/appointments')} className={mobileMenuItemClass}>
                        <Calendar className="h-4 w-4" />
                        {t('nav.appointments')}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/doctor/availability')} className={mobileMenuItemClass}>
                        <Clock className="h-4 w-4" />
                        {i18n.language === 'ar' ? 'التوفر والخدمات' : 'Availability & Services'}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/doctor/patients')} className={mobileMenuItemClass}>
                        <Users className="h-4 w-4" />
                        {i18n.language === 'ar' ? 'مرضاي' : 'My Patients'}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/doctor/reviews')} className={mobileMenuItemClass}>
                        <Star className="h-4 w-4" />
                        {i18n.language === 'ar' ? 'التقييمات' : 'Reviews'}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/doctor/profile')} className={mobileMenuItemClass}>
                        <Stethoscope className="h-4 w-4" />
                        {t('nav.myProfile')}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/clinic/dashboard')} className={mobileMenuItemClass}>
                        <Building2 className="h-4 w-4" />
                        {i18n.language === 'ar' ? 'إدارة العيادة' : 'Clinic Management'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => navigateFromMobileMenu('/profile')} className={mobileMenuItemClass}>
                        <User className="h-4 w-4" />
                        {t('nav.myProfile')}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/appointments')} className={mobileMenuItemClass}>
                        <Calendar className="h-4 w-4" />
                        {t('nav.appointments')}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/family')} className={mobileMenuItemClass}>
                        <Users className="h-4 w-4" />
                        {i18n.language === 'ar' ? 'أفراد العائلة' : 'Family Members'}
                      </button>
                      <button type="button" onClick={() => navigateFromMobileMenu('/medical-records')} className={mobileMenuItemClass}>
                        <FileText className="h-4 w-4" />
                        {i18n.language === 'ar' ? 'السجلات الطبية' : 'Medical Records'}
                      </button>
                    </>
                  )}

                  <button type="button" onClick={() => navigateFromMobileMenu('/settings')} className={mobileMenuItemClass}>
                    <Settings className="h-4 w-4" />
                    {i18n.language === 'ar' ? 'الإعدادات' : 'Settings'}
                  </button>
                  <button
                    type="button"
                    onClick={handleMobileLogout}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('common.logout')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 border-t my-2 pt-4 dark:border-slate-700">
                  <Button variant="outline" asChild data-testid="mobile-login-button">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>{t('common.login')}</Link>
                  </Button>
                  <Button className="bg-teal-600 hover:bg-teal-700" asChild data-testid="mobile-register-button">
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>{t('common.register')}</Link>
                  </Button>
                </div>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
